import { Fragment, useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'

// components
import { Alert } from '../../../common/Alert'
import { StickyTable } from '../../../common/StickyTable'
import { CustomPagination } from '../../../common/CustomPagination'
import { TableSkeleton } from '../../../skeletons/TableSkeleton'
import { TableHead, TableRow, TableCell, TableBody, styled, Box } from '@mui/material'
import { EmptyData } from '../../../common/EmptyData'

// others
import { AppContext } from '../../../../context'
import { ActionType } from '../../../../context/AppContextReducer'
import { formatCurrency } from '../../../../utils'
import palette from '../../../../theme/palette'
import { GRAPHQL_QUERY_POLICY, PAGE_LIMIT, SOMETHING_WENT_WRONG } from '../../../../constants'
import {
  FetchPaginatedAccountPaymentsQuery,
  PaymentType,
  useFetchPaginatedAccountPaymentsLazyQuery
} from '../../../../../generated'
import { flexCenter } from '../../../../theme/styleConstants'

const CustomLink = styled(Link)(() => ({
  color: palette.primary.main,
  textDecoration: 'underline'
}))

interface TransactionsTableProps {
  accountId: string
  triggerRefetch?: number
}

const TransactionsTable = ({ accountId, triggerRefetch }: TransactionsTableProps) => {
  const [payments, setPayments] = useState<
    FetchPaginatedAccountPaymentsQuery['fetchPaginatedAccountPayments']['payments']
  >([])
  const { currentPage, dispatch: appDispatch } = useContext(AppContext)

  const [fetchPayments, { loading }] = useFetchPaginatedAccountPaymentsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError(error) {
      Alert.error(SOMETHING_WENT_WRONG)
    },
    onCompleted(data) {
      const { fetchPaginatedAccountPayments } = data || {}
      const { payments, response, pagination } = fetchPaginatedAccountPayments || {}

      if (response?.status === 200) {
        setPayments(payments)
      }

      if (pagination) {
        const { totalPages, totalCount } = pagination
        totalPages && appDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages })
        totalCount && appDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount })
      }
    }
  })

  useEffect(() => {
    fetchPayments({
      variables: {
        PaginatedAccountPaymentsInput: {
          accountId: accountId,
          page: currentPage,
          limit: PAGE_LIMIT
        }
      }
    })
  }, [currentPage, fetchPayments, accountId, triggerRefetch])

  useEffect(() => {
    return () => {
      appDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
      appDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: 1 })
      appDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: 0 })
    }
  }, [appDispatch])

  const isEmpty = payments?.length === 0 && !loading
  if (isEmpty) {
    return (
      <Box sx={{ ...flexCenter, height: 'calc(100vh - 400px)' }}>
        <EmptyData title="No transactions found" />
      </Box>
    )
  }

  return (
    <StickyTable pagination={<CustomPagination loading={loading || false} />} height="400px">
      <TableHead>
        <TableRow>
          <TableCell> Source </TableCell>
          <TableCell> Date </TableCell>
          <TableCell> Client </TableCell>
          <TableCell> Matter# </TableCell>
          <TableCell> Bill# </TableCell>
          <TableCell> Vendor </TableCell>
          <TableCell> Funds Out </TableCell>
          <TableCell> Funds In </TableCell>
          <TableCell> Running Balance </TableCell>
        </TableRow>
      </TableHead>

      {loading ? (
        <TableSkeleton noOfRows={7} noOfCols={9} />
      ) : (
        <TableBody>
          {payments?.map(payment => {
            const {
              id,
              date,
              bill,
              case: litigation,
              vendor,
              client,
              amount,
              paymentType,
              runningBalance,
              linkedPayment
            } = payment
            const source = linkedPayment?.account?.name || 'Direct Payment'
            const fundsOut = paymentType === PaymentType.Debit ? formatCurrency(amount) : '-'
            const fundsIn = paymentType === PaymentType.Credit ? formatCurrency(amount) : '-'

            return (
              <Fragment key={id}>
                <TableRow>
                  <TableCell>{source}</TableCell>
                  <TableCell>{format(new Date(date), 'MM/dd/yyyy')}</TableCell>
                  <TableCell>{`${client?.firstName || ''} ${client?.lastName || ''}`}</TableCell>

                  <TableCell>
                    <CustomLink to={`/matters/matter-detail/${litigation?.id}`}>
                      {litigation?.caseNumber && `#${litigation?.caseNumber}`}
                    </CustomLink>
                  </TableCell>
                  <TableCell>
                    <CustomLink to={`/billing/bill-detail/${bill?.id}`}>{bill?.id && `#${bill?.id}`}</CustomLink>
                  </TableCell>
                  <TableCell>{vendor?.name}</TableCell>

                  <TableCell>{fundsOut}</TableCell>
                  <TableCell>{fundsIn}</TableCell>
                  <TableCell>{formatCurrency(runningBalance)}</TableCell>
                </TableRow>
              </Fragment>
            )
          })}
        </TableBody>
      )}
    </StickyTable>
  )
}

export default TransactionsTable
